/** Modello di visualizzazione: i gruppi non creano legami genealogici. */
export type AtlasPerson = {
  id: number; name: string; relation: string | null; photoMediaId: number | null;
  birth: string | null; death: string | null; status: 'confirmed' | 'proposed';
  gen: number | null; familyGroup: string | null; isMamma?: boolean; aliases: string[]; memories: number;
  sex?: 'M' | 'F' | null;
};
export type AtlasLink = {
  key: string; type: 'parent' | 'partner' | 'relation'; people: number[]; child: number | null;
  relationKind?: string; label?: string | null; note?: string | null;
  status: 'confirmed' | 'proposed' | 'rejected'; sourceMemoryId: number | null;
};
export type Atlas = {
  mamma: string; nodes: AtlasPerson[]; links: AtlasLink[]; version: string;
  narrator?: { personId: number | null; explicit: boolean };
  /** Fin dove l'albero illustrato mostra i posti vuoti (0–3); assente = 2, fino ai nonni. */
  vuotiFino?: number;
  history: Array<{ id: number; label: string; created_at: string; undone_at: string | null }>;
};
export type AtlasView = 'albero' | 'generazioni' | 'ramo' | 'mappa' | 'panoramica';
export const groupLabel = (gen: number | null): string => gen === null ? 'Altre persone care' : gen <= -2 ? 'Nonni e bisnonni' : gen === -1 ? 'Genitori, zii e zie' : gen === 0 ? 'La tua generazione' : gen === 1 ? 'Figli e nipoti' : 'Le nuove generazioni';
export const normalize = (s: string): string => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase('it').trim();
export const touches = (l: AtlasLink, id: number): boolean => l.people.includes(id) || l.child === id;
export function connectedBranch(id: number, links: AtlasLink[]): Set<number> {
  const seen = new Set([id]), queue = [id];
  while (queue.length) {
    const current = queue.pop()!;
    for (const l of links) if (touches(l, current)) for (const next of [...l.people, ...(l.child !== null ? [l.child] : [])]) {
      if (!seen.has(next)) { seen.add(next); queue.push(next); }
    }
  }
  return seen;
}
export function activeLinks(data: Atlas, proposed: boolean): AtlasLink[] {
  const visible = new Set(data.nodes.filter((n) => proposed || n.status === 'confirmed').map((n) => n.id));
  return data.links.filter((l) => l.status !== 'rejected' && (proposed || l.status === 'confirmed') && l.people.every((id) => visible.has(id)) && (l.child === null || visible.has(l.child)));
}
export function relatives(id: number, links: AtlasLink[]) {
  const parents = new Set<number>(), partners = new Set<number>(), children = new Set<number>(), siblings = new Set<number>();
  for (const l of links) {
    if (l.type === 'partner' && l.people.includes(id)) l.people.filter((p) => p !== id).forEach((p) => partners.add(p));
    if (l.type === 'parent' && l.child === id) l.people.forEach((p) => parents.add(p));
    if (l.type === 'parent' && l.people.includes(id)) children.add(l.child!);
  }
  for (const l of links) if (l.type === 'parent' && l.child !== id && l.people.some((p) => parents.has(p))) siblings.add(l.child!);
  return { parents, partners, children, siblings };
}
export function generations(people: AtlasPerson[]): Array<{ gen: number | null; people: AtlasPerson[] }> {
  const groups = new Map<number | null, AtlasPerson[]>();
  for (const p of people) groups.set(p.gen, [...(groups.get(p.gen) ?? []), p]);
  return [...groups].sort(([a], [b]) => (a ?? 999) - (b ?? 999)).map(([gen, people]) => ({ gen, people: people.sort((a, b) => a.name.localeCompare(b.name, 'it')) }));
}
export type Position = { x: number; y: number };
export const CARD_W = 204, CARD_H = 140, ROW_H = 244;
/** Ordine deterministico, coppie vicine e figli vicini al baricentro dei genitori.
 * Nessuna dipendenza, nessuna modifica ai gruppi corretti a mano. */
export function layout(people: AtlasPerson[], links: AtlasLink[]) {
  const rows = generations(people);
  const positions = new Map<number, Position>();
  const width = Math.max(500, ...rows.map((r) => r.people.length * (CARD_W + 32) + 72));
  rows.forEach((row, rowIndex) => {
    const score = (p: AtlasPerson): number => {
      const parents = links.filter((l) => l.type === 'parent' && l.child === p.id).flatMap((l) => l.people).map((id) => positions.get(id)?.x).filter((x): x is number => x !== undefined);
      return parents.length ? parents.reduce((a, b) => a + b, 0) / parents.length : width / 2;
    };
    row.people.sort((a, b) => score(a) - score(b) || a.name.localeCompare(b.name, 'it'));
    const ordered: AtlasPerson[] = [], remaining = new Map(row.people.map((p) => [p.id, p]));
    for (const p of row.people) {
      if (!remaining.delete(p.id)) continue;
      ordered.push(p);
      for (const l of links) if (l.type === 'partner' && l.people.includes(p.id)) {
        const partner = remaining.get(l.people.find((id) => id !== p.id)!);
        if (partner) { ordered.push(partner); remaining.delete(partner.id); }
      }
    }
    row.people = ordered;
    const start = (width - (ordered.length * (CARD_W + 32) - 32)) / 2;
    ordered.forEach((p, index) => positions.set(p.id, { x: start + index * (CARD_W + 32), y: rowIndex * ROW_H + 76 }));
  });
  return { rows, positions, width, height: Math.max(340, rows.length * ROW_H + 44) };
}

/** La panoramica avvolge i gruppi su più righe: niente strisce larghe decine
 * di schermi. Le righe restano gruppi di visualizzazione, non nuove parentele. */
export function overviewLayout(people: AtlasPerson[], links: AtlasLink[], aspect: number) {
  const rows = layout(people, links).rows, positions = new Map<number, Position>();
  const ratio = Math.max(.3, Math.min(3, aspect));
  const maxColumns = Math.max(2, Math.min(128, Math.max(0, ...rows.map((r) => r.people.length))));
  let columns = 2, bestScale = 0;
  for (let candidate = 2; candidate <= maxColumns; candidate++) {
    const width = candidate * (CARD_W + 32) + 80;
    const height = 76 + rows.reduce((sum, r) => sum + Math.ceil(r.people.length / candidate) * (CARD_H + 64) + 64, 0);
    const scale = Math.min(ratio / width, 1 / height);
    if (scale > bestScale) { columns = candidate; bestScale = scale; }
  }
  const width = columns * (CARD_W + 32) + 80;
  let y = 76;
  for (const row of rows) {
    row.people.forEach((p, i) => {
      const count = Math.min(columns, row.people.length - Math.floor(i / columns) * columns);
      positions.set(p.id, { x: (width - (count * (CARD_W + 32) - 32)) / 2 + (i % columns) * (CARD_W + 32), y: y + Math.floor(i / columns) * (CARD_H + 64) });
    });
    y += Math.ceil(row.people.length / columns) * (CARD_H + 64) + 64;
  }
  return { rows, positions, width, height: Math.max(340, y) };
}
